import React from 'react';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import type { Theme } from '../../contexts/themeTypes';

interface SplitMapViewProps {
  splitMapContainerRef: React.RefObject<HTMLDivElement | null>;
  onCenterOnCircuit: () => void;
  onResetMapView: () => void;
  /** Light/dark chrome for Mapbox controls to match the map basemap */
  mapBasemapTheme: Theme;
}

/**
 * The left map component in split view mode.
 * Contains only the map container and a minimal toolbar with center/reset buttons.
 * The Mapbox NavigationControl is added by the useSplitMap hook.
 */
export const SplitMapView = ({
  splitMapContainerRef,
  onCenterOnCircuit,
  onResetMapView,
  mapBasemapTheme,
}: SplitMapViewProps) => {
  const t = mapBasemapTheme;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Map container */}
      <div 
        ref={splitMapContainerRef} 
        style={{ width: '100%', height: '100%' }} 
      />

      {/* Custom toolbar with center and reset buttons - positioned below NavigationControl */}
      <div style={{
        position: 'absolute',
        top: 110, // Below Mapbox navigation control
        right: 10,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}>
        {/* Map navigation - center and reset */}
        <div className="mapboxgl-ctrl mapboxgl-ctrl-group" style={{
          backgroundColor: t === 'dark' ? '#1f1f1f' : '#fff',
          boxShadow: t === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)',
          borderRadius: 4,
        }}>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={onCenterOnCircuit}
            title="Center on Circuit"
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              borderBottom: t === 'dark' ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.1)',
            }}
          >
            <EnvironmentOutlined style={{ fontSize: '15px', color: t === 'dark' ? '#fff' : '#333' }} />
          </button>
          <button
            type="button"
            className="mapboxgl-ctrl-icon"
            onClick={onResetMapView}
            title="Reset Map View"
            style={{
              width: '29px',
              height: '29px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            <AimOutlined style={{ fontSize: '15px', color: t === 'dark' ? '#fff' : '#333' }} />
          </button>
        </div>
      </div>

      {/* Mapbox control styles for this map */}
      <style>{`
        .mapboxgl-ctrl-group {
          background-color: ${t === 'dark' ? '#1f1f1f' : '#fff'} !important;
          box-shadow: ${t === 'dark' ? '0 0 0 2px rgba(255,255,255,.1)' : '0 0 0 2px rgba(0,0,0,.1)'} !important;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
        }
        .mapboxgl-ctrl-group button:hover {
          background-color: ${t === 'dark' ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.05)'} !important;
        }
        .mapboxgl-ctrl-group button + button {
          border-top: 1px solid ${t === 'dark' ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)'} !important;
        }
        .mapboxgl-ctrl-zoom-in .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-zoom-out .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
          filter: ${t === 'dark' ? 'invert(1)' : 'none'};
        }
        .mapboxgl-ctrl-attrib {
          background-color: ${t === 'dark' ? 'rgba(31, 31, 31, 0.8)' : 'rgba(255, 255, 255, 0.8)'} !important;
        }
        .mapboxgl-ctrl-attrib a {
          color: ${t === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.75)'} !important;
        }
        .mapboxgl-ctrl-attrib-inner {
          color: ${t === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)'} !important;
        }
        .mapboxgl-ctrl-attrib-button {
          filter: ${t === 'dark' ? 'invert(1)' : 'none'};
        }
      `}</style>
    </div>
  );
};
